import React, { useRef, useState } from "react";
import { looksLikeCsv } from "../../../../services/contactImportService";

interface ContactImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Exactly one of text/csv, matching POST /contacts/import's body shape. */
  onImport: (input: { text: string } | { csv: string }) => Promise<void>;
  isImporting?: boolean;
  error?: string | null;
}

// Matches contact_ingest/app.py's MAX_BODY_BYTES — catch an oversized file
// here instead of letting it round-trip to the backend just to 413.
const MAX_FILE_BYTES = 300_000;

export const ContactImportModal: React.FC<ContactImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isImporting = false,
  error,
}) => {
  const [raw, setRaw] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const loadFile = (file: File) => {
    setFileError(null);
    if (!/\.(csv|txt)$/i.test(file.name) && file.type && !file.type.includes("csv") && !file.type.includes("text")) {
      setFileError("That doesn't look like a CSV or text file.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError(`File is too large (${Math.round(file.size / 1024)} KB) — max ${MAX_FILE_BYTES / 1000} KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setRaw(String(reader.result ?? ""));
    reader.onerror = () => setFileError("Couldn't read that file — try pasting its contents instead.");
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadFile(file);
    e.target.value = ""; // allow re-selecting the same file later
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadFile(file);
  };

  const handleSubmit = async () => {
    const trimmed = raw.trim();
    if (!trimmed || isImporting) return;
    await onImport(looksLikeCsv(trimmed) ? { csv: trimmed } : { text: trimmed });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 flex w-full max-w-lg flex-col rounded-2xl border border-theme-border bg-theme-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-theme-fg">Import Contacts</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-theme-fg-muted hover:bg-theme-background hover:text-theme-fg"
            aria-label="Close"
          >
            <span className="material-icons-outlined text-[20px]">close</span>
          </button>
        </div>
        <p className="mb-3 text-[12.5px] text-theme-fg-muted">
          Paste a contact list, drop a CSV file, or browse for one. You&rsquo;ll review, fix, and drop entries before anything writes to GHL.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          className={`relative rounded-xl border transition-colors ${
            isDragOver ? "border-theme-primary bg-theme-primary/5" : "border-theme-border"
          }`}
        >
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={"Jane Doe, jane@acme.com, (555) 214-7788\nBob Smith, bob@beta.com\n…or paste/drop a CSV export"}
            rows={8}
            disabled={isImporting}
            className="w-full resize-none rounded-xl bg-theme-background px-3 py-2.5 text-[13px] text-theme-fg outline-none disabled:opacity-60"
          />
          {isDragOver && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-theme-surface/90">
              <p className="flex items-center gap-2 text-[13px] font-bold text-theme-primary">
                <span className="material-icons-outlined text-[20px]">upload_file</span>
                Drop CSV to load
              </p>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isImporting}
          className="mt-2 inline-flex w-fit items-center gap-1.5 text-[12px] font-bold text-theme-primary hover:opacity-80 disabled:opacity-60"
        >
          <span className="material-icons-outlined text-[15px]">attach_file</span>
          Upload a CSV file
        </button>

        {fileError && <p className="mt-2 text-[12px] font-medium text-theme-danger">{fileError}</p>}
        {error && <p className="mt-2 text-[12px] font-medium text-theme-danger">{error}</p>}

        <div className="mt-4 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            className="rounded-xl border border-theme-border px-4 py-2.5 text-[13px] font-bold text-theme-fg hover:bg-theme-background disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isImporting || !raw.trim()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-theme-primary px-4 py-2.5 text-[13px] font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isImporting && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {isImporting ? "Parsing…" : "Parse Contacts"}
          </button>
        </div>
      </div>
    </div>
  );
};
